import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../components/ui/pagination";
import {
  Plus,
  Search,
  Edit,
  Package,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_view_ingredients,
  get_view_units,
  show_hide_ingredients,
  save_update_ingredients,
  get_department_with_categories,
  get_purchase_master_data,
} from "../ServiceConnection/serviceconnection";
import { SearchableSelect } from "../components/ui/searchable-select";

// Kitchen/recipe units — free-standing list, not tied to the base Units master.
// "spelling sahi kar lena" -> fixed here once, used everywhere below.
const RECIPE_UNIT_OPTIONS = ["Spoon", "Pinch", "Leaf", "Scoop", "Piece", "Ladle"];

export default function ViewIngredient() {
  const { toast } = useToast();

  // ── Refs for form inputs ────────────────────────────────────────────────
  const ingredientNameRef = useRef(null);
  const ingredientAlterNameRef = useRef(null);
  const defaultUnitRef = useRef(null);
  const departmentRef = useRef(null);
  const categoryRef = useRef(null);
  const eachContainerRef = useRef(null);
  const recipeUnitNameRef = useRef(null);
  const recipeUnitQtyRef = useRef(null);

  // ── States ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [purchaseTypes, setPurchaseTypes] = useState([]);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [departments, setDepartments] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    ingredientName: "",
    defaultUnit: "",
    openStock: "0",
    total_stock: "0",
    department_id: "",
    department_name: "",
    category_id: "",
    category_name: "",
    minimumStockAlert: "",
    isActive: true,
    needOwnerApproval: false,
    purchase_type_name: "",
    each_container: "",
    // ── Recipe Unit ──────────────────────────────────────────────────────
    recipe_unit_active: false,
    recipe_unit_name: "",
    recipe_unit_qty_in_base: "",
  });

  const [errors, setErrors] = useState({});

  // ── Helpers ──────────────────────────────────────────────────────────────
  const isCategoryMandatory = useCallback(() => {
    if (!formData.department_id) return false;
    const selectedDept = departments.find(
      (dept) => String(dept.department_id) === formData.department_id,
    );
    return !selectedDept?.department_name?.toLowerCase().includes("bar");
  }, [formData.department_id, departments]);

  const filteredIngredients = useMemo(() => {
    if (!ingredients?.length) return [];

    const query = searchQuery.toLowerCase().trim();

    return ingredients
      .filter((ingredient) => {
        if (!query) return true;
        return (
          ingredient.ingredient_name.toLowerCase().includes(query) ||
          ingredient.default_unit?.toLowerCase().includes(query) ||
          ingredient.unit_code?.toLowerCase().includes(query) ||
          ingredient.purchase_type?.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name));
  }, [ingredients, searchQuery]);

  const totalItems = filteredIngredients.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredIngredients.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const resetForm = useCallback(() => {
    setFormData({
      ingredientName: "",
      defaultUnit: "",
      openStock: "0",
      total_stock: "0",
      minimumStockAlert: "",
      isActive: true,
      needOwnerApproval: false,
      purchase_type_name: "",
      each_container: "",
      department_id: "",
      department_name: "",
      category_id: "",
      category_name: "",
      recipe_unit_active: false,
      recipe_unit_name: "",
      recipe_unit_qty_in_base: "",
    });
    setErrors({});
    setEditingIngredient(null);
    setCategoryOptions([]);
  }, []);

  const handleFieldChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (field === "department_id") {
        const selectedDept = departments.find(
          (dept) => String(dept.department_id) === value,
        );
        if (selectedDept) {
          setFormData((prev) => ({
            ...prev,
            department_id: value,
            department_name: selectedDept.department_name,
          }));
          const mappedCategories = (selectedDept.categories || []).map(
            (cat) => ({
              value: String(cat.primary_id),
              label:
                cat.dcategory_name ||
                cat.category_name ||
                `Category ${cat.primary_id}`,
            }),
          );
          setCategoryOptions(mappedCategories);
          setFormData((prev) => ({
            ...prev,
            category_id: "",
            category_name: "",
          }));
        }
      }

      if (field === "category_id") {
        const selectedCat = categoryOptions.find((opt) => opt.value === value);
        if (selectedCat) {
          setFormData((prev) => ({
            ...prev,
            category_id: value,
            category_name: selectedCat.label,
          }));
        }
      }

      // Toggling recipe unit off clears its dependent fields so a stale
      // name/qty never gets submitted silently while the section is hidden.
      if (field === "recipe_unit_active" && value === false) {
        setFormData((prev) => ({
          ...prev,
          recipe_unit_active: false,
          recipe_unit_name: "",
          recipe_unit_qty_in_base: "",
        }));
        setErrors((prev) => {
          const { recipe_unit_name, recipe_unit_qty_in_base, ...rest } = prev;
          return rest;
        });
      }

      setErrors((prev) => {
        const { [field]: removed, ...rest } = prev;
        return rest;
      });
    },
    [departments, categoryOptions],
  );

  const focusField = useCallback((ref) => {
    if (!ref?.current) return;

    setTimeout(() => {
      // Scroll into view first
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // After scrolling, focus the element
      setTimeout(() => {
        if (ref.current) {
          // Try direct focus first (works for Input elements)
          if (typeof ref.current.focus === "function") {
            ref.current.focus();
          } else {
            // For Select/SearchableSelect, find the button/input element
            const button = ref.current.querySelector("button");
            const input = ref.current.querySelector("input");

            if (button) {
              button.focus();
            } else if (input) {
              input.focus();
            }
          }
        }
      }, 300); // Wait for smooth scroll to complete
    }, 100);
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    let firstErrorRef = null;

    // if (!formData.department_id) {
    //   newErrors.department_id = "Department is required";
    //   if (!firstErrorRef) firstErrorRef = departmentRef;
    // }

    if (isCategoryMandatory() && !formData.category_id) {
      newErrors.category_id = "Category is required for this department";
      if (!firstErrorRef) firstErrorRef = categoryRef;
    }

    if (!formData.ingredientName.trim()) {
      newErrors.ingredientName = "Ingredient name is required";
      if (!firstErrorRef) firstErrorRef = ingredientNameRef;
    }

    if (!formData.ingredientAlterName.trim()) {
      newErrors.ingredientAlterName = "Ingredient Alternative name is required";
      if (!firstErrorRef) firstErrorRef = ingredientAlterNameRef;
    }

    if (!formData.defaultUnit) {
      newErrors.defaultUnit = "Default unit is required";
      if (!firstErrorRef) firstErrorRef = defaultUnitRef;
    }

    // Validation for each_container
    if (
      formData.purchase_type_name &&
      formData.purchase_type_name !== "Loose"
    ) {
      if (!formData.each_container.trim()) {
        newErrors.each_container = "Quantity per container is required";
        if (!firstErrorRef) firstErrorRef = eachContainerRef;
      } else if (parseFloat(formData.each_container) <= 0) {
        newErrors.each_container = "Must be greater than 0";
        if (!firstErrorRef) firstErrorRef = eachContainerRef;
      }
    }

    // Validation for Recipe Unit — only required when the toggle is on
    if (formData.recipe_unit_active) {
      if (!formData.recipe_unit_name) {
        newErrors.recipe_unit_name = "Select a recipe unit";
        if (!firstErrorRef) firstErrorRef = recipeUnitNameRef;
      }
      if (
        !formData.recipe_unit_qty_in_base ||
        parseFloat(formData.recipe_unit_qty_in_base) <= 0
      ) {
        newErrors.recipe_unit_qty_in_base = "Enter a quantity greater than 0";
        if (!firstErrorRef) firstErrorRef = recipeUnitQtyRef;
      }
    }

    setErrors(newErrors);

    // Focus on first error field
    if (Object.keys(newErrors).length > 0 && firstErrorRef) {
      focusField(firstErrorRef);
    }

    return Object.keys(newErrors).length === 0;
  }, [formData, isCategoryMandatory, focusField]);

  // ── Fetch functions ──────────────────────────────────────────────────────
  const fetchPurchaseTypes = useCallback(async () => {
    const fd = new FormData();
    fd.append("status", "0");
    fd.append("search", "");
    try {
      const response = await server_post_data(get_purchase_master_data, fd);
      if (!response.data.error && response.data.message?.purchase_type) {
        const formatted = response.data.message.purchase_type.map((pt) => ({
          primary_id: String(pt.primary_id),
          purchase_type_name: pt.purchase_type_name,
        }));
        setPurchaseTypes(formatted);
      }
    } catch (error) {
      console.error("Error fetching purchase types:", error);
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const response = await server_post_data(get_view_units, fd);
      if (!response.data.error) {
        const formattedUnits = (response.data.message || []).map((item) => ({
          primary_id: item.primary_id,
          unit_name: item.unit_name,
          short_code: item.short_code_name,
          active_status: item.active_status === 1,
        }));
        setUnits(formattedUnits);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const response = await server_post_data(
        get_department_with_categories,
        fd,
      );
      if (!response.data.error) {
        setDepartments(response.data.message.data || []);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  const fetchIngredients = useCallback(async () => {
    if (units.length === 0) return;
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const response = await server_post_data(get_view_ingredients, fd);
      if (!response.data.error && response.data.message) {
        const apiIngredients = response.data.message.ingredients_data || [];
        const formattedIngredients = apiIngredients.map((item) => {
          const matchedUnit = units.find(
            (unit) =>
              parseInt(unit.primary_id) === parseInt(item.default_unit_id),
          );
          return {
            primary_id: item.primary_id,
            ingredient_name: item.ingredient_name,
            ingredient_alter_name: item.ingredient_alter_name,
            default_unit: matchedUnit?.unit_name || "Unknown Unit",
            unit_code: matchedUnit?.short_code || "N/A",
            open_stock: item.open_stock || 0,
            minimum_stock_alert: item.minimum_stock_alert || 0,
            balance_stock: item.balance_stock || 0,
            active_status:
              item.active_status == 1 || item.active_status === "1",
            default_unit_id: item.default_unit_id,
            purchase_type: item.purchase_type || "",
            each_container: item.each_container || "",
            // ── Recipe Unit ──────────────────────────────────────────────────
            recipe_unit_active:
              item.recipe_unit_active === 1 || item.recipe_unit_active === "1",
            recipe_unit_name: item.recipe_unit_name || "",
            recipe_unit_qty_in_base: item.recipe_unit_qty_in_base || 0,
          };
        });
        setIngredients(formattedIngredients);
      }
    } catch (error) {
      console.error("Error fetching ingredients:", error);
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, [units]);

  const handleCreateClick = useCallback(() => {
    resetForm();
    setIsFormDialogOpen(true);
  }, [resetForm]);

  const handleEditClick = useCallback(
    async (ingredient) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("main_id", ingredient.primary_id);
      try {
        const response = await server_post_data(get_view_ingredients, fd);
        if (response.data.error || !response.data.message?.ingredients_data) {
          toast({
            title: "Error",
            description: "Failed to load ingredient",
            variant: "destructive",
          });
          return;
        }

        const data = response.data.message.ingredients_data;

        if (data.department_id) {
          const dept = departments.find(
            (d) => String(d.department_id) === String(data.department_id),
          );
          if (dept?.categories?.length > 0) {
            const mapped = dept.categories.map((cat) => ({
              value: String(cat.primary_id),
              label:
                cat.dcategory_name ||
                cat.category_name ||
                `Category ${cat.primary_id}`,
            }));
            setCategoryOptions(mapped);
          }
        }

        setEditingIngredient(ingredient);
        setFormData({
          ingredientName: data.ingredient_name || "",
          ingredientAlterName: data.ingredient_alter_name || "",
          sameAsAlternative: data.sameasalternative || "",
          defaultUnit: data.default_unit_id?.toString() || "",
          openStock: data.open_stock?.toString() || "0",
          total_stock: data.total_stock?.toString() || "0",
          minimumStockAlert: data.minimum_stock_alert?.toString() || "",
          isActive: data.active_status === 1 || data.active_status === "1",
          department_id: data.department_id?.toString() || "",
          category_id: data.dcategory_id?.toString() || "",
          needOwnerApproval:
            data.need_owner_approval === 1 || data.need_owner_approval === "1",
          purchase_type_name: data.purchase_type || "",
          each_container: data.each_container?.toString() || "",
          // ── Recipe Unit ────────────────────────────────────────────────
          recipe_unit_active:
            data.recipe_unit_active === 1 || data.recipe_unit_active === "1",
          recipe_unit_name: data.recipe_unit_name || "",
          recipe_unit_qty_in_base:
            data.recipe_unit_qty_in_base != null
              ? String(data.recipe_unit_qty_in_base)
              : "",
        });
        setErrors({});
        setIsFormDialogOpen(true);
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "Network error",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast, departments],
  );

  const handleSaveChanges = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("ingredient_name", formData.ingredientName.trim());
    fd.append("ingredient_alter_name", formData.ingredientAlterName.trim());
    fd.append("sameasalternative", formData.sameAsAlternative);
    fd.append("default_unit_id", formData.defaultUnit);
    fd.append("open_stock", formData.openStock || "0");
    fd.append("open_stock_unit_id", formData.defaultUnit);
    fd.append("minimum_stock_alert", formData.minimumStockAlert || "0");
    fd.append("active_status", formData.isActive ? "1" : "0");
    fd.append("need_owner_approval", formData.needOwnerApproval ? "1" : "0");
    fd.append("dcategory_id", formData.category_id || "0");
    fd.append("purchase_type", formData.purchase_type_name || "");
    fd.append("each_container", formData.each_container || "");
    // ── Recipe Unit ──────────────────────────────────────────────────────
    fd.append("recipe_unit_active", formData.recipe_unit_active ? "1" : "0");
    fd.append(
      "recipe_unit_name",
      formData.recipe_unit_active ? formData.recipe_unit_name : "",
    );
    fd.append(
      "recipe_unit_qty_in_base",
      formData.recipe_unit_active ? formData.recipe_unit_qty_in_base : "0",
    );

    if (editingIngredient) {
      fd.append("main_id", editingIngredient.primary_id);
    }

    try {
      const response = await server_post_data(save_update_ingredients, fd);
      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Save failed",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editingIngredient
            ? "Updated successfully"
            : "Created successfully",
        });
        fetchIngredients();
        setIsFormDialogOpen(false);
        resetForm();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    formData,
    editingIngredient,
    toast,
    fetchIngredients,
    resetForm,
  ]);

  const handleToggleClick = useCallback((ingredient) => {
    setSelectedIngredient(ingredient);
    setStatusChangeMessage(
      ingredient.active_status
        ? "Are you sure you want to deactivate this ingredient?"
        : "Are you sure you want to activate this ingredient?",
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedIngredient) return;
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("ingredient_id", selectedIngredient.primary_id);
    console.log("Toggling status for ingredient ID:", selectedIngredient.primary_id);
    console.log("New active_status:", selectedIngredient.active_status ? "0" : "1");
    fd.append("active_status", selectedIngredient.active_status ? "0" : "1");

    try {
      const response = await server_post_data(show_hide_ingredients, fd);
      if (!response.data.error) {
        setIngredients((prev) =>
          prev.map((item) =>
            item.primary_id === selectedIngredient.primary_id
              ? { ...item, active_status: !selectedIngredient.active_status }
              : item,
          ),
        );
        toast({
          title: "Success",
          description: `Ingredient ${
            selectedIngredient.active_status ? "deactivated" : "activated"
          }`,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedIngredient(null);
    }
  }, [selectedIngredient, toast]);
 const isLowStock = (ingredient) => {
  console.log("Open Stock:", ingredient.open_stock);
  console.log("Min Alert:", ingredient.minimum_stock_alert);
  console.log("Balance Stock:", ingredient.balance_stock);

  return (
    ingredient.minimum_stock_alert > 0 &&
    ingredient.balance_stock <= ingredient.minimum_stock_alert
  );
};

  const selectedUnitDetails = units.find(
    (unit) => unit.primary_id === parseInt(formData.defaultUnit),
  );
  const isEditMode = editingIngredient !== null;
  const hasStock = parseFloat(formData.openStock) > 0;
  const hasTotalStock = parseFloat(formData.total_stock) > 0;

  useEffect(() => {
    fetchUnits();
    fetchDepartments();
    fetchPurchaseTypes();
  }, [fetchUnits, fetchDepartments, fetchPurchaseTypes]);

  useEffect(() => {
    if (units.length > 0) fetchIngredients();
  }, [units, fetchIngredients]);

  return (
    <MainLayout>
      <Header
        title="Items Management"
        subtitle="Manage your restaurant items and inventory"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {ingredients.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Items</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {ingredients.filter((i) => i.active_status).length}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {ingredients.filter((i) => isLowStock(i)).length}
                </p>
                <p className="text-sm text-muted-foreground">Low Stock</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Package className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {ingredients.filter((i) => !i.active_status).length}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search ingredients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={handleCreateClick}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add New Item
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr.No</TableHead>
                <TableHead className="text-muted-foreground">
                  Ingredient Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Default Unit
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Unit Code
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Open Stock
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Balance Stock
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Min Stock Alert
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Purchase Type
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Each Container
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-12 text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : currentItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-12 text-muted-foreground"
                  >
                    No items found
                  </TableCell>
                </TableRow>
              ) : (
                currentItems.map((ingredient, index) => (
                  <TableRow
                    key={ingredient.primary_id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="font-medium">
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                          <Package className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div className="flex flex-col gap-0.5"> 
  {/* Flex-col use kiya hai taaki name upar aur alert niche rahe, 
      lekin agar aapko sab ek hi line mein chahiye toh 'flex-row items-center gap-2' use karein */}
  
  <div className="flex items-baseline gap-1 flex-wrap">
    <p className="font-medium text-foreground text-sm">
      {ingredient.ingredient_name}
    </p>
    
    {ingredient.ingredient_alter_name && 
     ingredient.ingredient_alter_name.trim() !== ingredient.ingredient_name.trim() && (
      <span className="text-muted-foreground text-[10px] leading-none">
        ({ingredient.ingredient_alter_name})
      </span>
    )}
  </div>

  {isLowStock(ingredient) && (
    <p className="text-[10px] text-warning flex items-center gap-1 font-medium">
      <AlertTriangle className="w-3 h-3" />
      Low Stock Alert
    </p>
  )}
</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex flex-col gap-0.5">
                        <span>{ingredient.default_unit}</span>
                        {ingredient.recipe_unit_active && ingredient.recipe_unit_name && (
                          <span className="text-[10px] text-cyan-600 font-medium">
                            1 {ingredient.recipe_unit_name} = {ingredient.recipe_unit_qty_in_base}{" "}
                            {ingredient.default_unit}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {ingredient.unit_code}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "font-medium",
                          isLowStock(ingredient)
                            ? "text-warning"
                            : "text-foreground",
                        )}
                      >
                        {ingredient.open_stock}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "font-medium",
                          isLowStock(ingredient)
                            ? "text-warning"
                            : "text-foreground",
                        )}
                      >
                        {ingredient.balance_stock}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ingredient.minimum_stock_alert || "N/A"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ingredient.purchase_type || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ingredient.purchase_type &&
                      ingredient.purchase_type !== "Loose"
                        ? `${ingredient.each_container || "?"} ${
                            ingredient.default_unit
                          }`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleClick(ingredient)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                          ingredient.active_status
                            ? "bg-success"
                            : "bg-muted-foreground/30",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            ingredient.active_status
                              ? "translate-x-6"
                              : "translate-x-1",
                          )}
                        />
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(ingredient)}
                          className="hover:bg-primary/10 hover:text-primary"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                entries per page
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage > 1) {
                            setCurrentPage((prev) => prev - 1);
                          }
                        }}
                        className={cn(
                          currentPage === 1 && "pointer-events-none opacity-50",
                        )}
                      />
                    </PaginationItem>

                    {getPageNumbers().map((pageNum, idx) =>
                      pageNum === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(pageNum);
                            }}
                            isActive={currentPage === pageNum}
                            className={cn(
                              "cursor-pointer",
                              currentPage === pageNum &&
                                "border-primary bg-primary/10 text-primary hover:bg-primary/20",
                            )}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      ),
                    )}

                    <PaginationItem>
                      <PaginationNext
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage < totalPages) {
                            setCurrentPage((prev) => prev + 1);
                          }
                        }}
                        className={cn(
                          currentPage === totalPages &&
                            "pointer-events-none opacity-50",
                        )}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </div>
        )}

        {/* Form Dialog */}
        <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isEditMode ? "Edit Ingredient" : "Add New Ingredient"}
              </DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? "Update ingredient details and stock information"
                  : "Add a new ingredient to your inventory"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-foreground">
                  Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ingredientName">
                      Ingredient Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      ref={ingredientNameRef}
                      id="ingredientName"
                      placeholder="e.g., Chivas Regal, Atta, Pyaz"
                      value={formData.ingredientName}
                      onChange={(e) => {
                        handleFieldChange("ingredientName", e.target.value);
                        if (formData.sameAsAlternative === 1) {
                          handleFieldChange("ingredientAlterName", e.target.value);
                        }
                      }}
                      className={errors.ingredientName ? "border-destructive" : ""}
                    />
                    {errors.ingredientName && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {errors.ingredientName}
                      </p>
                    )}
                    {/* Checkbox - chota sa niche */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        id="sameAsAlternative"
                        checked={formData.sameAsAlternative === 1}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          handleFieldChange("sameAsAlternative", checked ? 1 : 0);
                          if (checked) handleFieldChange("ingredientAlterName", formData.ingredientName);
                        }}
                        className="w-3 h-3 cursor-pointer"
                      />
                      <label htmlFor="sameAsAlternative" className="text-xs text-muted-foreground cursor-pointer">
                        Same as alternative name
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ingredientAlterName">
                      Ingredient Alternative Name{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      ref={ingredientAlterNameRef}
                      id="ingredientAlterName"
                      placeholder="e.g., Chivas Regal, Atta, Pyaz"
                      value={formData.ingredientAlterName}
                      onChange={(e) =>
                        handleFieldChange("ingredientAlterName", e.target.value)
                      }
                      readOnly={formData.sameAsAlternative === 1}
                      className={`${errors.ingredientAlterName ? "border-destructive" : ""} ${formData.sameAsAlternative === 1 ? "bg-muted text-muted-foreground cursor-not-allowed" : ""}`}
                     
                    />
                    {errors.ingredientAlterName && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{" "}
                        {errors.ingredientAlterName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2" ref={defaultUnitRef}>
                    <Label htmlFor="defaultUnit">
                      Default Unit <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.defaultUnit}
                      onValueChange={(value) =>
                        handleFieldChange("defaultUnit", value)
                      }
                      disabled={isEditMode}
                    >
                      <SelectTrigger
                        className={cn(
                          errors.defaultUnit ? "border-destructive" : "",
                          isEditMode && "bg-muted cursor-not-allowed",
                        )}
                      >
                        <SelectValue placeholder="Select Default Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map((unit) => (
                          <SelectItem
                            key={unit.primary_id}
                            value={unit.primary_id.toString()}
                          >
                            {unit.unit_name} ({unit.short_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.defaultUnit && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {errors.defaultUnit}
                      </p>
                    )}
                    {isEditMode && (
                      <p className="text-xs text-destructive">
                        Default unit cannot be changed after creation
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="purchaseType">Purchase Type</Label>
                    <Select
                      value={formData.purchase_type_name}
                      onValueChange={(value) =>
                        handleFieldChange("purchase_type_name", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Purchase Type (Optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {purchaseTypes.map((pt) => (
                          <SelectItem
                            key={pt.primary_id}
                            value={pt.purchase_type_name}
                          >
                            {pt.purchase_type_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      e.g., Loose, Bottle, Packets, Tin, Jar
                    </p>
                  </div>

                  {/* <div className="space-y-2" ref={departmentRef}>
                    <Label htmlFor="department">
                      Department <span className="text-destructive">*</span>
                    </Label>
                    <SearchableSelect
                      options={
                        departments?.map((dept) => ({
                          value: String(dept.department_id),
                          label: dept.department_name,
                        })) || []
                      }
                      value={formData.department_id}
                      onValueChange={(value) =>
                        handleFieldChange("department_id", value)
                      }
                      placeholder="Select Department"
                      searchPlaceholder="Search departments..."
                      className={cn(
                        errors.department_id && "border-destructive",
                      )}
                      disabled={isEditMode}
                    />
                    {errors.department_id && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{" "}
                        {errors.department_id}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2" ref={categoryRef}>
                    <Label htmlFor="category">
                      Sub-Departments{" "}
                      {isCategoryMandatory() && (
                        <span className="text-destructive">*</span>
                      )}
                    </Label>
                    <SearchableSelect
                      options={categoryOptions}
                      value={formData.category_id}
                      onValueChange={(value) =>
                        handleFieldChange("category_id", value)
                      }
                      placeholder={
                        !formData.department_id
                          ? "First select Department"
                          : categoryOptions.length === 0
                            ? "No categories available"
                            : isCategoryMandatory()
                              ? "Select Category"
                              : "Select Category (Optional)"
                      }
                      searchPlaceholder="Search categories..."
                      disabled={
                        !formData.department_id ||
                        categoryOptions.length === 0 ||
                        isEditMode
                      }
                      className={cn(errors.category_id && "border-destructive")}
                    />
                    {errors.category_id && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {errors.category_id}
                      </p>
                    )}
                  </div> */}

                  

                  {/* Each Container Field */}
                  {formData.purchase_type_name &&
                    formData.purchase_type_name !== "Loose" && (
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="eachContainer">
                          Quantity per{" "}
                          {formData.purchase_type_name.toLowerCase()}{" "}
                          <span className="text-destructive">*</span>
                          {selectedUnitDetails && (
                            <span className="text-muted-foreground text-xs ml-2">
                              (in {selectedUnitDetails.unit_name})
                            </span>
                          )}
                        </Label>
                        <Input
                          ref={eachContainerRef}
                          id="eachContainer"
                          type="text"
                          placeholder="e.g., 50, 200, 1, 5"
                          value={formData.each_container}
                          onChange={(e) =>
                            /^\d*\.?\d*$/.test(e.target.value) &&
                            handleFieldChange("each_container", e.target.value)
                          }
                          className={cn(
                            errors.each_container && "border-destructive",
                          )}
                        />
                        {errors.each_container && (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />{" "}
                            {errors.each_container}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          How much of this item comes in one{" "}
                          {formData.purchase_type_name.toLowerCase()}?
                        </p>
                      </div>
                    )}
                </div>
              </div>

              {/* Stock Information */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-foreground">
                  Stock Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="openStock">
                      Open Stock{" "}
                      {selectedUnitDetails && (
                        <span className="text-muted-foreground text-xs">
                          ({selectedUnitDetails.unit_name})
                        </span>
                      )}
                    </Label>
                    <Input
                      id="openStock"
                      type="text"
                      placeholder="Opening stock quantity"
                      value={formData.openStock}
                      onChange={(e) =>
                        /^\d*\.?\d*$/.test(e.target.value) &&
                        handleFieldChange("openStock", e.target.value)
                      }
                      readOnly={isEditMode && hasTotalStock}
                      className={cn(
                        isEditMode && hasTotalStock && "bg-muted cursor-not-allowed",
                      )}
                    />
                    {isEditMode && hasTotalStock && (
                      <p className="text-xs text-destructive">
                        Open Stock cannot be edited when stock value is greater
                        than 0
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="minimumStockAlert">
                      Minimum Stock Alert{" "}
                      {selectedUnitDetails && (
                        <span className="text-muted-foreground text-xs">
                          ({selectedUnitDetails.unit_name})
                        </span>
                      )}
                    </Label>
                    <Input
                      id="minimumStockAlert"
                      type="text"
                      placeholder="e.g., 5, 2, 10, 50"
                      value={formData.minimumStockAlert}
                      onChange={(e) =>
                        /^\d*\.?\d*$/.test(e.target.value) &&
                        handleFieldChange("minimumStockAlert", e.target.value)
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Get alerted when stock falls below this quantity
                      (Optional)
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-foreground">
                  Approval Settings
                </h4>
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div>
                    <Label className="cursor-pointer">
                      Require Owner Approval for Purchase
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      If enabled, any indent for this ingredient will require
                      owner approval
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleFieldChange(
                        "needOwnerApproval",
                        !formData.needOwnerApproval,
                      )
                    }
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      formData.needOwnerApproval
                        ? "bg-success"
                        : "bg-muted-foreground/30",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                        formData.needOwnerApproval
                          ? "translate-x-6"
                          : "translate-x-1",
                      )}
                    />
                  </button>
                </div>
              </div>

              {/* Recipe Unit — kitchen unit like Spoon/Pinch/Leaf for this ingredient */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-foreground">
                  Recipe Unit
                </h4>
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div>
                    <Label className="cursor-pointer">
                      Enable Recipe Unit for this Ingredient
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Lets chefs enter quantities in kitchen units (Spoon,
                      Pinch, Leaf...) instead of {selectedUnitDetails?.unit_name || "the base unit"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleFieldChange(
                        "recipe_unit_active",
                        !formData.recipe_unit_active,
                      )
                    }
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      formData.recipe_unit_active
                        ? "bg-success"
                        : "bg-muted-foreground/30",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                        formData.recipe_unit_active
                          ? "translate-x-6"
                          : "translate-x-1",
                      )}
                    />
                  </button>
                </div>

                {formData.recipe_unit_active && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in-50 duration-200">
                    <div className="space-y-2" ref={recipeUnitNameRef}>
                      <Label htmlFor="recipeUnitName">
                        Recipe Unit <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.recipe_unit_name}
                        onValueChange={(value) =>
                          handleFieldChange("recipe_unit_name", value)
                        }
                      >
                        <SelectTrigger
                          className={
                            errors.recipe_unit_name ? "border-destructive" : ""
                          }
                        >
                          <SelectValue placeholder="Select recipe unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {RECIPE_UNIT_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.recipe_unit_name && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{" "}
                          {errors.recipe_unit_name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="recipeUnitQty">
                        Quantity{" "}
                        {selectedUnitDetails && (
                          <span className="text-muted-foreground text-xs">
                            ({selectedUnitDetails.unit_name})
                          </span>
                        )}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        ref={recipeUnitQtyRef}
                        id="recipeUnitQty"
                        type="text"
                        placeholder="e.g., 12, 0.35, 55"
                        value={formData.recipe_unit_qty_in_base}
                        onChange={(e) =>
                          /^\d*\.?\d*$/.test(e.target.value) &&
                          handleFieldChange(
                            "recipe_unit_qty_in_base",
                            e.target.value,
                          )
                        }
                        className={
                          errors.recipe_unit_qty_in_base
                            ? "border-destructive"
                            : ""
                        }
                      />
                      {errors.recipe_unit_qty_in_base && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{" "}
                          {errors.recipe_unit_qty_in_base}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        1 {formData.recipe_unit_name || "unit"} ={" "}
                        {formData.recipe_unit_qty_in_base || "?"}{" "}
                        {selectedUnitDetails?.unit_name || "base unit"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Status - Only in Add mode */}
              {!isEditMode && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-foreground">
                    Status
                  </h4>
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div>
                      <Label htmlFor="isActive" className="cursor-pointer">
                        Active Status{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Toggle to activate or deactivate this ingredient
                      </p>
                    </div>
                    <button
                      id="isActive"
                      onClick={() =>
                        handleFieldChange("isActive", !formData.isActive)
                      }
                      className={cn(
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                        formData.isActive
                          ? "bg-success"
                          : "bg-muted-foreground/30",
                      )}
                    >
                      <span
                        className={cn(
                          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                          formData.isActive ? "translate-x-6" : "translate-x-1",
                        )}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsFormDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin
                  ? "Saving..."
                  : isEditMode
                    ? "Update Ingredient"
                    : "Create Ingredient"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toggle Confirmation */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Ingredient Status</AlertDialogTitle>
              <AlertDialogDescription>
                {statusChangeMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? "Updating..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}